// A question object for rhythm interactives (So far including meterID, completeTheMeasure, labelCounts)

import RhythmReading from './rhythmReading';
import processRhythm from '../uTheory.Skills/processRhythm';
import Music from '../uTheory.Notation/music';
import { getTopic } from '../uTheory.Skills/skills';
import type Pointer from '../uTheory.Notation/pointer';
import type { QuestionObject } from './uPrefs';
import type TimeSignature from '../uTheory.Notation/timeSignature';

export interface RhythmQuestionObject extends QuestionObject {
  musicString: string;
  id: string | null;
}

export interface QuestionBankMeasureRhythm extends RhythmQuestionObject {
  createdAt: string;
  isCompound: boolean;
  isCutTime: boolean;
  meter: string;
  meterId: boolean;
  missingNotes: boolean;
  numMissingNotes?: number[];
}

export default abstract class RhythmQuestionProcessor {
  id: string;

  /** skillIds in string format */
  abstract skills: string[];

  /** as [lowestTempo, highestTempo] */
  tempoRange: [number, number];

  /** music object representing the question's notation */
  music: Music;

  /** the timeSignature of the music */
  timeSignature: TimeSignature;

  /** string or string[] to replace {question} in interactive.questionPrompt */
  text: string[];

  /** Rhythm or music string for this question */
  rhythmOrMusicString: string;

  /** Tempo it'll be played back as */
  get tempo() {
    const [lowTempo, highTempo] = this.tempoRange;
    return (lowTempo + highTempo) >> 1;
  }

  get stopPointer(): Pointer {
    const pointers = this.music.voices[0].getPerformancePointers().slice(-1)[0];
    return pointers.startPointer.clone().add(pointers.durationPointer);
  }

  /** in seconds */
  get stopTime() {
    const beats = this.stopPointer.toBeats();
    const beatSec = 60 / this.tempo;
    const seconds = beats * beatSec;
    return seconds;
  }

  /**
   * Create a Question from a rhythmString.
   * @param {string} rhythmString in format such as:
   *   RhythmString: "12/8: tempo=62 q. q. h. | h. e e e e e e | h. h.r",
   *     or "6/8: e e e q. | e e e q. | q. q. | e e e q."
   *   MusicString: See 'Potential music storage format.md'
   */
  constructor(rhythmOrMusicString: string, questionNumber: number);
  /**
   * Create a Question from an object -- typically this is one pulled from database of questions.
   * @param {Object} questionOBject
   * @param {string} Object.musicString
   * @param {string} Object.id
   */
  constructor(questionObject: RhythmQuestionObject, questionNumber: number);
  constructor(rhythmOrMusicStringOrObject: string | RhythmQuestionObject, questionNumber: number);
  constructor(rhythmOrMusicStringOrObject: string | RhythmQuestionObject, questionNumber: number) {
    let rhythmOrMusicString: string;
    let id: string;
    if (typeof rhythmOrMusicStringOrObject === 'string') {
      rhythmOrMusicString = rhythmOrMusicStringOrObject;
      id = null;
    } else {
      rhythmOrMusicString = rhythmOrMusicStringOrObject.musicString;
      id = rhythmOrMusicStringOrObject.id;
    }

    const { music, tempoRange } = this.processQuestionString(rhythmOrMusicString);

    const timeSignature: TimeSignature = music.getTimeSignature(0);
    // console.log('timeSignature', timeSignature);

    const text = [questionNumber + 1];
    Object.assign(this, {
      music,
      tempoRange,
      timeSignature,
      id,
      text,
      rhythmOrMusicString,
    });
  }

  /**
   * @param {string} rhythmOrMusicString
   * @returns {{ music: Music, tempoRange: [number, number]}}
   */
  processQuestionString(rhythmOrMusicString: string): {
    music: Music;
    tempoRange: [number, number];
  } {
    const music = rhythmOrMusicString.includes('clef')
      ? Music.fromString(rhythmOrMusicString)
      : this.rhythmStringToMusic(rhythmOrMusicString);

    const tempoRange = this.getTempoRange(music);

    return { tempoRange, music };
  }

  rhythmStringToMusic(rhythmString: string): Music {
    const { musicString } = RhythmReading.prototype.manualQuestionStringToRhythm.call(
      this,
      rhythmString
    );
    // console.log('My string:');
    // console.log(musicString);
    return Music.fromString(musicString);
  }

  /**
   * @param {Music} music
   * @returns {[ number, number ]} [ lowTempo, highTempo ]
   */
  getTempoRange(music: Music): [number, number] {
    // console.log(music);
    const skills = processRhythm(music.voices[0], music.getTimeSignature(0));
    const reducer = ([lowTempo, highTempo]: [number, number], skill): [number, number] => {
      if (!skill.id) return [lowTempo, highTempo];
      const topic = getTopic(skill.topicId);
      if (!topic || !topic.tempo) return [lowTempo, highTempo]; // should never happen...
      const [newLow, newHigh] = topic.tempo;
      lowTempo = Math.max(lowTempo, newLow);
      highTempo = Math.min(highTempo, newHigh);
      return [lowTempo, highTempo];
    };
    // GCR TODO: I don't know why we have to coerce this... prior to TS 2.9.2 we didn't have to. Sigh.
    return skills.reduce(reducer, [1, 300]) as [number, number];
  }
}
